import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, StaggerItem, Overline, SectionShell } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { SERVICES, SERVICE_DETAILS, IMAGES } from "../content";

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <main data-testid="services-page">
      <PageHero
        overline="OUR SERVICES"
        title="Six disciplines."
        highlight="One geospatial value chain."
        desc="From the first satellite capture to the production database, DCS delivers comprehensive solutions across data creation, conversion, enhancement, software development, modeling, analytics and consulting."
        image={IMAGES.satellite_matrix}
      />

      <SectionShell className="pt-4">
        <Stagger className="space-y-6">
          {SERVICES.map((s, i) => {
            const detail = SERVICE_DETAILS[s.id];
            return (
              <StaggerItem key={s.id}>
                <article
                  id={s.id}
                  data-testid={`service-detail-${s.id}`}
                  className="scroll-mt-28 border border-white/10 rounded-3xl bg-[#0B0E14]/70 backdrop-blur-md overflow-hidden hover:border-cyan-500/30 transition-colors duration-300"
                >
                  <div className="grid lg:grid-cols-[1fr_1.4fr]">
                    <div className="p-8 sm:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-gradient-to-br from-[#111622] to-transparent">
                      <span className="font-mono-tech text-xs text-cyan-500 tracking-[0.25em]">{detail.overline}</span>
                      <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white mt-4 tracking-tight">{detail.title}</h2>
                      <p className="mt-4 text-slate-300 text-sm sm:text-base leading-relaxed font-medium">{detail.hero}</p>
                      <p className="mt-3 text-slate-500 text-sm leading-relaxed">{detail.intro}</p>
                      <div className="mt-6 flex flex-wrap gap-2">
                        {s.features.map((f) => (
                          <span key={f} className="font-mono-tech text-[10px] tracking-wider text-cyan-300/80 bg-cyan-400/5 border border-cyan-400/15 rounded-full px-3 py-1.5 uppercase">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="p-8 sm:p-10">
                      <div className="space-y-6">
                        {detail.blocks.map((b) => (
                          <div key={b.h} className="flex gap-4">
                            <CheckCircle2 size={18} className="text-cyan-400 shrink-0 mt-1" />
                            <div>
                              <h3 className="font-display text-base sm:text-lg font-bold text-slate-100">{b.h}</h3>
                              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{b.p}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </Stagger>
      </SectionShell>
      <CtaSection />
    </main>
  );
}
